import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  CircularProgress,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Chip,
  Tabs,
  Tab,
  Box,
  TextField,
  Grid,
  Card,
  CardContent,
  Tooltip,
  Alert,
  Snackbar,
  Badge,
  Switch,
  FormControlLabel
} from '@mui/material';
import {
  Add as AddIcon,
  CloudDownload as DownloadIcon,
  Delete as DeleteIcon,
  Folder as FolderIcon,
  Person as PersonIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  ArrowBack as ArrowBackIcon,
  RequestPage as RequestPageIcon,
  CheckCircle as CheckCircleIcon,
  HourglassEmpty as PendingIcon,
  ChildCare as ChildCareIcon,
  FamilyRestroom as FamilyIcon,
  Cancel as CancelIcon,
  Visibility as ViewIcon,
  NotificationsActive as NotifyIcon
} from '@mui/icons-material';
import { storage, db } from '../firebase';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import {
  collection,
  query,
  getDocs,
  addDoc,
  deleteDoc,
  doc,
  updateDoc,
  where,
  serverTimestamp
} from 'firebase/firestore';
import Menu from '@mui/material/Menu';
import { useNavigate } from 'react-router-dom';

// ─── Constantes ────────────────────────────────────────────────────────────────

const PREDEFINED_REQUEST_TYPES = [
  { id: 'mutuelle', label: 'Document mutuelle', icon: '🏥' },
  { id: 'contrat', label: 'Contrat', icon: '📄' },
  { id: 'carte_identite', label: "Carte d'identité", icon: '🪪' },
  { id: 'clinique', label: 'Document clinique / médical', icon: '🩺' },
  { id: 'autorisation_parentale', label: 'Autorisation parentale', icon: '✍️' },
  { id: 'certificat_medical', label: 'Certificat médical', icon: '📋' },
  { id: 'justificatif_domicile', label: 'Justificatif de domicile', icon: '🏠' },
  { id: 'photo', label: 'Photo', icon: '🖼️' },
  { id: 'autre', label: 'Autre document', icon: '📎' }
];

const REQUEST_STATUS = {
  pending: { label: 'En attente', color: 'warning', icon: <PendingIcon fontSize="small" /> },
  received: { label: 'Reçu', color: 'success', icon: <CheckCircleIcon fontSize="small" /> },
  rejected: { label: 'Refusé', color: 'error', icon: <CancelIcon fontSize="small" /> }
};

// ─── Composant principal ────────────────────────────────────────────────────────

const Documents = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('students');
  const [students, setStudents] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [accountingDocs, setAccountingDocs] = useState([]);
  const [loading, setLoading] = useState(true);

  // Upload admin
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [documentCategory, setDocumentCategory] = useState('');
  const [visibleByUser, setVisibleByUser] = useState(false);

  // Catégories
  const [categories, setCategories] = useState({ students: [], teachers: [], accounting: [] });
  const [newCategoryName, setNewCategoryName] = useState('');
  const [isAddingCategory, setIsAddingCategory] = useState(false);
  const [editCategory, setEditCategory] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [currentCategory, setCurrentCategory] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Demandes de documents
  const [documentRequests, setDocumentRequests] = useState([]);
  const [openRequestDialog, setOpenRequestDialog] = useState(false);
  const [selectedStudentForRequest, setSelectedStudentForRequest] = useState(null);
  const [requestType, setRequestType] = useState('');
  const [requestCustomLabel, setRequestCustomLabel] = useState('');
  const [requestNote, setRequestNote] = useState('');
  const [requestsView, setRequestsView] = useState('list'); // 'list' | 'by_student'
  const [selectedStudentRequests, setSelectedStudentRequests] = useState(null);
  const [viewDocDialog, setViewDocDialog] = useState(null);

  // Feedback
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // ─── Chargement données ───────────────────────────────────────────────────────

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Élèves — chargées EN PARALLÈLE plutôt qu'une par une : avec un
        // for...of + await séquentiel, N élèves = N allers-retours réseau
        // l'un après l'autre (c'était la cause du chargement très long de
        // cette page). Promise.all lance toutes les requêtes en même temps.
        const studentsSnapshot = await getDocs(collection(db, 'students'));
        const studentsData = await Promise.all(
          studentsSnapshot.docs.map(async (docRef) => {
            const student = docRef.data();
            const docsSnapshot = await getDocs(collection(db, `students/${docRef.id}/documents`));
            return {
              id: docRef.id,
              ...student,
              documents: docsSnapshot.docs.map(d => ({ id: d.id, ...d.data() }))
            };
          })
        );

        // Professeurs — même correctif
        const teachersSnapshot = await getDocs(collection(db, 'teachers'));
        const teachersData = await Promise.all(
          teachersSnapshot.docs.map(async (docRef) => {
            const teacher = docRef.data();
            const docsSnapshot = await getDocs(collection(db, `teachers/${docRef.id}/documents`));
            return {
              id: docRef.id,
              ...teacher,
              documents: docsSnapshot.docs.map(d => ({ id: d.id, ...d.data() }))
            };
          })
        );

        // Comptabilité
        const accountingSnapshot = await getDocs(collection(db, 'accounting_documents'));
        const accountingData = accountingSnapshot.docs.map(d => ({ id: d.id, ...d.data() }));

        // Demandes de documents
        const requestsSnapshot = await getDocs(collection(db, 'document_requests'));
        const requestsData = requestsSnapshot.docs.map(d => ({ id: d.id, ...d.data() }));

        setStudents(studentsData);
        setTeachers(teachersData);
        setAccountingDocs(accountingData);
        setDocumentRequests(requestsData);
        setLoading(false);
      } catch (error) {
        console.error('Erreur de chargement:', error);
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const snapshot = await getDocs(collection(db, 'document_categories'));
      const cats = { students: [], teachers: [], accounting: [] };
      snapshot.forEach(d => {
        const data = d.data();
        if (data.type && cats[data.type]) cats[data.type].push({ id: d.id, ...data });
      });
      setCategories(cats);
    } catch (error) {
      console.error('Erreur catégories:', error);
    }
  };

  // ─── Gestion catégories ───────────────────────────────────────────────────────

  const handleMenuOpen = (event, category) => {
    setAnchorEl(event.currentTarget);
    setCurrentCategory(category);
  };
  const handleMenuClose = () => { setAnchorEl(null); setCurrentCategory(null); };

  const handleAddCategory = async () => {
    if (!newCategoryName.trim()) return;
    try {
      await addDoc(collection(db, 'document_categories'), {
        name: newCategoryName.trim(), type: activeTab, createdAt: new Date()
      });
      setNewCategoryName('');
      setIsAddingCategory(false);
      await fetchCategories();
    } catch (error) { console.error(error); }
  };

  const handleEditCategory = async () => {
    if (!editCategory?.name.trim()) return;
    try {
      await updateDoc(doc(db, 'document_categories', editCategory.id), { name: editCategory.name });
      setEditCategory(null);
      await fetchCategories();
    } catch (error) { console.error(error); }
  };

  const handleDeleteCategory = async () => {
    if (!window.confirm(`Supprimer la catégorie "${currentCategory.name}"?`)) return;
    try {
      if (currentCategory.type === 'accounting') {
        const q = query(collection(db, 'accounting_documents'), where('categoryId', '==', currentCategory.id));
        const snap = await getDocs(q);
        if (snap.size > 0) { alert('Des documents existent dans cette catégorie'); return; }
      } else {
        const usersSnap = await getDocs(collection(db, currentCategory.type));
        let total = 0;
        for (const userDoc of usersSnap.docs) {
          const userDocsSnap = await getDocs(query(
            collection(db, `${currentCategory.type}/${userDoc.id}/documents`),
            where('categoryId', '==', currentCategory.id)
          ));
          total += userDocsSnap.size;
        }
        if (total > 0) { alert('Des documents existent dans cette catégorie'); return; }
      }
      await deleteDoc(doc(db, 'document_categories', currentCategory.id));
      handleMenuClose();
      await fetchCategories();
    } catch (error) { console.error(error); }
  };

  // ─── Upload fichier ───────────────────────────────────────────────────────────

  const handleOpenDialog = (itemId, type, category) => {
    setSelectedItem({ id: itemId, type, category });
    if (categories[type]?.length > 0) setDocumentCategory(categories[type][0].id);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setFile(null);
    setSelectedItem(null);
    setDocumentCategory('');
    setVisibleByUser(false);
  };

  const handleUpload = async () => {
    if (!file || !selectedItem) return;
    setUploading(true);
    try {
      const timestamp = Date.now();
      const uniqueFileName = `${timestamp}_${file.name}`;
      const category = categories[selectedItem.type]?.find(c => c.id === documentCategory);
      if (!category) { setUploading(false); return; }

      let downloadURL, docRef;

      if (selectedItem.type === 'students' || selectedItem.type === 'teachers') {
        const storageRef = ref(storage, `documents/${selectedItem.type}/${selectedItem.id}/${uniqueFileName}`);
        const snapshot = await uploadBytes(storageRef, file);
        downloadURL = await getDownloadURL(snapshot.ref);
        docRef = await addDoc(collection(db, `${selectedItem.type}/${selectedItem.id}/documents`), {
          fileName: file.name, fileType: file.type, downloadURL,
          categoryId: category.id, categoryName: category.name,
          createdAt: new Date(), storagePath: storageRef.fullPath,
          uploadedBy: 'admin',
          visibleByUser
        });
        const setUser = selectedItem.type === 'students' ? setStudents : setTeachers;
        setUser(prev => prev.map(u => u.id === selectedItem.id
          ? { ...u, documents: [...u.documents, { id: docRef.id, fileName: file.name, fileType: file.type, downloadURL, categoryId: category.id, categoryName: category.name, storagePath: storageRef.fullPath, uploadedBy: 'admin', visibleByUser }] }
          : u));
      } else {
        const storageRef = ref(storage, `documents/accounting/${uniqueFileName}`);
        const snapshot = await uploadBytes(storageRef, file);
        downloadURL = await getDownloadURL(snapshot.ref);
        docRef = await addDoc(collection(db, 'accounting_documents'), {
          fileName: file.name, fileType: file.type, downloadURL,
          categoryId: category.id, categoryName: category.name,
          createdAt: new Date(), storagePath: storageRef.fullPath
        });
        setAccountingDocs(prev => [...prev, { id: docRef.id, fileName: file.name, fileType: file.type, downloadURL, categoryId: category.id, categoryName: category.name, storagePath: storageRef.fullPath }]);
      }
      handleCloseDialog();
      setSnackbar({ open: true, message: 'Document uploadé avec succès', severity: 'success' });
    } catch (error) {
      console.error(error);
      setSnackbar({ open: true, message: "Erreur lors de l'upload", severity: 'error' });
    }
    setUploading(false);
  };

  const handleDelete = async (itemId, documentId, storagePath, type) => {
    if (!window.confirm('Supprimer ce document définitivement?')) return;
    try {
      const fileRef = ref(storage, storagePath);
      await deleteObject(fileRef);
      if (type === 'students' || type === 'teachers') {
        await deleteDoc(doc(db, `${type}/${itemId}/documents/${documentId}`));
        const setUser = type === 'students' ? setStudents : setTeachers;
        setUser(prev => prev.map(u => u.id === itemId
          ? { ...u, documents: u.documents.filter(d => d.id !== documentId) } : u));
      } else {
        await deleteDoc(doc(db, `accounting_documents/${documentId}`));
        setAccountingDocs(prev => prev.filter(d => d.id !== documentId));
      }
      setSnackbar({ open: true, message: 'Document supprimé', severity: 'success' });
    } catch (error) {
      console.error(error);
      setSnackbar({ open: true, message: 'Erreur lors de la suppression', severity: 'error' });
    }
  };

  // ─── Demandes de documents ────────────────────────────────────────────────────

  const handleOpenRequestDialog = (student) => {
    setSelectedStudentForRequest(student);
    setRequestType('');
    setRequestCustomLabel('');
    setRequestNote('');
    setOpenRequestDialog(true);
  };

  const handleCreateRequest = async () => {
    if (!selectedStudentForRequest || !requestType) return;
    try {
      const predefined = PREDEFINED_REQUEST_TYPES.find(t => t.id === requestType);
      const label = requestType === 'autre' && requestCustomLabel.trim()
        ? requestCustomLabel.trim()
        : predefined?.label || requestType;

      // ⚠️ On utilise appUserId (= Firebase Auth UID) pour que l'app Flutter
      // puisse retrouver la demande via request.auth.uid.
      // Si appUserId est absent (élève créé manuellement), on fallback sur l'id Firestore.
      const authUid = selectedStudentForRequest.appUserId
        || selectedStudentForRequest.uid
        || selectedStudentForRequest.id;

      const newRequest = {
        studentId: authUid,
        firestoreStudentId: selectedStudentForRequest.id, // conservé pour référence admin
        studentName: `${selectedStudentForRequest.firstName} ${selectedStudentForRequest.lastName}`,
        isMinor: selectedStudentForRequest.isMinor || false,
        guardianName: selectedStudentForRequest.guardianName || null,
        guardianUserId: selectedStudentForRequest.guardianUserId || null,
        requestType,
        label,
        note: requestNote.trim(),
        status: 'pending',
        createdAt: serverTimestamp(),
        uploadedFileURL: null,
        uploadedFileName: null,
        uploadedAt: null
      };

      const docRef = await addDoc(collection(db, 'document_requests'), newRequest);
      setDocumentRequests(prev => [...prev, { id: docRef.id, ...newRequest, createdAt: new Date() }]);
      setOpenRequestDialog(false);
      setSnackbar({ open: true, message: `Demande envoyée à ${newRequest.studentName}`, severity: 'success' });
    } catch (error) {
      console.error(error);
      setSnackbar({ open: true, message: 'Erreur lors de la création de la demande', severity: 'error' });
    }
  };

  const handleUpdateRequestStatus = async (requestId, newStatus) => {
    try {
      await updateDoc(doc(db, 'document_requests', requestId), { status: newStatus });
      setDocumentRequests(prev => prev.map(r => r.id === requestId ? { ...r, status: newStatus } : r));
      setSnackbar({ open: true, message: 'Statut mis à jour', severity: 'success' });
    } catch (error) {
      console.error(error);
    }
  };

  const handleDeleteRequest = async (requestId) => {
    if (!window.confirm('Supprimer cette demande?')) return;
    try {
      await deleteDoc(doc(db, 'document_requests', requestId));
      setDocumentRequests(prev => prev.filter(r => r.id !== requestId));
    } catch (error) { console.error(error); }
  };

  // ─── Utilitaires ─────────────────────────────────────────────────────────────

  const filteredUsers = () => {
    const users = activeTab === 'students' ? students : teachers;
    if (!searchTerm) return users;
    return users.filter(u =>
      u.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.email?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  const getPendingRequestsCount = (studentId) =>
    documentRequests.filter(r => r.studentId === studentId && r.status === 'pending').length;

  const getReceivedRequestsCount = (studentId) =>
    documentRequests.filter(r => r.studentId === studentId && r.status === 'received').length;

  // ─── Rendu : Demandes (onglet global) ────────────────────────────────────────

  const renderRequestsPanel = () => {
    const allPending = documentRequests.filter(r => r.status === 'pending');
    const allReceived = documentRequests.filter(r => r.status === 'received');

    return (
      <Box>
        {/* Stats */}
        <Grid container spacing={2} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={4}>
            <Card sx={{ borderLeft: '4px solid #F59E0B', bgcolor: '#FFFBEB' }}>
              <CardContent sx={{ py: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PendingIcon sx={{ color: '#F59E0B' }} />
                  <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#B45309' }}>{allPending.length}</Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">Demandes en attente</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card sx={{ borderLeft: '4px solid #10B981', bgcolor: '#ECFDF5' }}>
              <CardContent sx={{ py: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CheckCircleIcon sx={{ color: '#10B981' }} />
                  <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#065F46' }}>{allReceived.length}</Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">Documents reçus</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card sx={{ borderLeft: '4px solid #7C3AED', bgcolor: '#F5F3FF' }}>
              <CardContent sx={{ py: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <RequestPageIcon sx={{ color: '#7C3AED' }} />
                  <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#5B21B6' }}>{documentRequests.length}</Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">Total demandes</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Table des demandes */}
        <TableContainer component={Paper} elevation={2}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: '#EDE9FE' }}>
                <TableCell sx={{ fontWeight: 'bold' }}>Élève</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Profil</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Document demandé</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Note</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Statut</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Fichier reçu</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {documentRequests.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary', fontStyle: 'italic' }}>
                    Aucune demande de document
                  </TableCell>
                </TableRow>
              ) : (
                documentRequests.map(request => {
                  const statusInfo = REQUEST_STATUS[request.status] || REQUEST_STATUS.pending;
                  const predefined = PREDEFINED_REQUEST_TYPES.find(t => t.id === request.requestType);
                  return (
                    <TableRow key={request.id} hover>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{request.studentName}</Typography>
                      </TableCell>
                      <TableCell>
                        {request.isMinor ? (
                          <Tooltip title={`Mineur – géré par : ${request.guardianName || 'parent'}`}>
                            <Chip
                              icon={<ChildCareIcon />}
                              label="Mineur"
                              size="small"
                              sx={{ bgcolor: '#FEF3C7', color: '#92400E' }}
                            />
                          </Tooltip>
                        ) : (
                          <Chip
                            icon={<PersonIcon />}
                            label="Adulte"
                            size="small"
                            sx={{ bgcolor: '#EDE9FE', color: '#5B21B6' }}
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <span>{predefined?.icon || '📎'}</span>
                          <Typography variant="body2">{request.label}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {request.note || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={statusInfo.icon}
                          label={statusInfo.label}
                          color={statusInfo.color}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        {request.uploadedFileURL ? (
                          <Tooltip title={request.uploadedFileName || 'Voir le fichier'}>
                            <IconButton href={request.uploadedFileURL} target="_blank" size="small">
                              <DownloadIcon color="primary" fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <Typography variant="body2" color="text.disabled">—</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          {request.status === 'pending' && request.uploadedFileURL && (
                            <Tooltip title="Marquer comme reçu">
                              <IconButton size="small" onClick={() => handleUpdateRequestStatus(request.id, 'received')} color="success">
                                <CheckCircleIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          {request.status !== 'pending' && (
                            <Tooltip title="Remettre en attente">
                              <IconButton size="small" onClick={() => handleUpdateRequestStatus(request.id, 'pending')}>
                                <PendingIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          <Tooltip title="Supprimer la demande">
                            <IconButton size="small" onClick={() => handleDeleteRequest(request.id)} color="error">
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };

  // ─── Rendu : Liste élèves avec demandes ───────────────────────────────────────

  const renderStudentsWithRequests = () => {
    const users = filteredUsers();
    const cats = categories[activeTab] || [];

    return (
      <Box sx={{ mt: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
            Liste des {activeTab === 'students' ? 'élèves' : 'professeurs'}
          </Typography>
          <TextField
            label={`Rechercher un ${activeTab === 'students' ? 'élève' : 'professeur'}`}
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ width: 300 }}
          />
        </Box>

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: '#EDE9FE' }}>
                <TableCell sx={{ fontWeight: 'bold' }}>Nom</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Profil</TableCell>
                {cats.map(cat => (
                  <TableCell key={cat.id} align="center" sx={{ fontWeight: 'bold' }}>{cat.name}</TableCell>
                ))}
                <TableCell align="center" sx={{ fontWeight: 'bold' }}>Docs total</TableCell>
                {activeTab === 'students' && (
                  <>
                    <TableCell align="center" sx={{ fontWeight: 'bold', color: '#F59E0B' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                        <PendingIcon fontSize="small" />
                        Demandes
                      </Box>
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold', color: '#10B981' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                        <CheckCircleIcon fontSize="small" />
                        Reçus
                      </Box>
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold', color: '#7C3AED' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                        📤 Docs élève
                      </Box>
                    </TableCell>
                  </>
                )}
                <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map(user => {
                const pending = activeTab === 'students' ? getPendingRequestsCount(user.id) : 0;
                const received = activeTab === 'students' ? getReceivedRequestsCount(user.id) : 0;

                return (
                  <TableRow key={user.id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PersonIcon sx={{ color: '#7C3AED' }} />
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {user.firstName} {user.lastName}
                        </Typography>
                      </Box>
                    </TableCell>

                    <TableCell>
                      {activeTab === 'students' && (
                        user.isMinor ? (
                          <Tooltip title={`Parent/tuteur : ${user.guardianName || 'non renseigné'}`}>
                            <Chip icon={<ChildCareIcon />} label="Mineur" size="small" sx={{ bgcolor: '#FEF3C7', color: '#92400E' }} />
                          </Tooltip>
                        ) : (
                          <Chip icon={<PersonIcon />} label="Adulte" size="small" sx={{ bgcolor: '#EDE9FE', color: '#5B21B6' }} />
                        )
                      )}
                    </TableCell>

                    {cats.map(cat => {
                      const count = user.documents.filter(d => d.categoryId === cat.id).length;
                      return (
                        <TableCell key={cat.id} align="center">
                          {count > 0
                            ? <Chip label={count} size="small" sx={{ bgcolor: '#7C3AED', color: 'white' }} onClick={() => setSelectedCategory(cat.name)} />
                            : <Typography variant="body2" color="text.disabled">0</Typography>
                          }
                        </TableCell>
                      );
                    })}

                    <TableCell align="center">
                      <Chip label={user.documents.length} size="small" sx={{ bgcolor: '#7C3AED', color: 'white' }} />
                    </TableCell>

                    {activeTab === 'students' && (
                      <>
                        <TableCell align="center">
                          {pending > 0
                            ? <Chip label={pending} size="small" color="warning" />
                            : <Typography variant="body2" color="text.disabled">0</Typography>
                          }
                        </TableCell>
                        <TableCell align="center">
                          {received > 0
                            ? <Chip label={received} size="small" color="success" />
                            : <Typography variant="body2" color="text.disabled">0</Typography>
                          }
                        </TableCell>
                        <TableCell align="center">
                          {(() => {
                            const userDocsCount = user.documents.filter(d => d.uploadedBy === 'user').length;
                            return userDocsCount > 0
                              ? <Chip label={userDocsCount} size="small" sx={{ bgcolor: '#7C3AED', color: 'white' }} />
                              : <Typography variant="body2" color="text.disabled">0</Typography>;
                          })()}
                        </TableCell>
                      </>
                    )}

                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                          variant="outlined"
                          startIcon={<AddIcon />}
                          onClick={() => handleOpenDialog(user.id, activeTab)}
                          size="small"
                        >
                          Ajouter
                        </Button>
                        {activeTab === 'students' && (
                          <Button
                            variant="contained"
                            startIcon={<RequestPageIcon />}
                            onClick={() => handleOpenRequestDialog(user)}
                            size="small"
                            sx={{ bgcolor: '#7C3AED', '&:hover': { bgcolor: '#6D28D9' } }}
                          >
                            Demander
                          </Button>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };

  // ─── Rendu : Catégories ───────────────────────────────────────────────────────

  const renderDocumentCategories = () => {
    const cats = categories[activeTab] || [];

    return (
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 'bold' }}>Catégories de documents</Typography>
          <Button variant="outlined" startIcon={<AddIcon />} onClick={() => setIsAddingCategory(true)}>
            Nouvelle catégorie
          </Button>
        </Box>

        {isAddingCategory && (
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <TextField
              autoFocus label="Nom de la nouvelle catégorie" variant="outlined" size="small"
              value={newCategoryName} onChange={(e) => setNewCategoryName(e.target.value)}
              sx={{ flexGrow: 1, mr: 2 }}
            />
            <Button variant="contained" onClick={handleAddCategory} disabled={!newCategoryName.trim()}
              sx={{ bgcolor: '#7C3AED', '&:hover': { bgcolor: '#6D28D9' } }}>Ajouter</Button>
            <Button variant="outlined" onClick={() => setIsAddingCategory(false)} sx={{ ml: 1 }}>Annuler</Button>
          </Box>
        )}

        <Grid container spacing={2}>
          {cats.map(cat => {
            const totalDocs = (activeTab === 'students' ? students : teachers)
              .reduce((acc, u) => acc + u.documents.filter(d => d.categoryId === cat.id).length, 0);

            return (
              <Grid item xs={12} sm={6} md={4} key={cat.id}>
                <Paper elevation={2} sx={{ p: 2, cursor: 'pointer', '&:hover': { boxShadow: 4 } }}
                  onClick={() => setSelectedCategory(cat.name)}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <FolderIcon sx={{ color: '#7C3AED' }} />
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>{cat.name}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Chip label={`${totalDocs} doc${totalDocs !== 1 ? 's' : ''}`} size="small" sx={{ bgcolor: '#7C3AED', color: 'white' }} />
                      <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleMenuOpen(e, cat); }}>
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                </Paper>
              </Grid>
            );
          })}
        </Grid>

        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
          <MenuItem onClick={() => { setEditCategory(currentCategory); handleMenuClose(); }}>
            <EditIcon sx={{ mr: 1 }} /> Modifier
          </MenuItem>
          <MenuItem onClick={handleDeleteCategory}>
            <DeleteIcon sx={{ mr: 1, color: 'error.main' }} /> Supprimer
          </MenuItem>
        </Menu>
      </Box>
    );
  };

  // ─── Rendu : Détail catégorie ─────────────────────────────────────────────────

  const renderCategoryDocuments = () => {
    const users = filteredUsers();
    const cats = categories[activeTab] || [];
    const category = cats.find(c => c.name === selectedCategory);
    if (!category) return null;

    return (
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <IconButton onClick={() => setSelectedCategory(null)} sx={{ mr: 1 }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h5" sx={{ fontWeight: 'bold' }}>{category.name}</Typography>
        </Box>

        <Grid container spacing={3}>
          {users.map(user => {
            const userDocs = user.documents.filter(d => d.categoryId === category.id);
            if (!userDocs.length) return null;
            return (
              <Grid item xs={12} key={user.id}>
                <Paper sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <PersonIcon sx={{ mr: 1, color: '#7C3AED' }} />
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>{user.firstName} {user.lastName}</Typography>
                    <Chip label={`${userDocs.length} document(s)`} size="small" sx={{ ml: 2, bgcolor: '#7C3AED', color: 'white' }} />
                  </Box>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 'bold' }}>Document</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>Type</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>Envoyé par</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>Note</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {userDocs.map(document => (
                          <TableRow key={document.id} hover sx={document.uploadedBy !== 'admin' ? { bgcolor: '#F5F3FF' } : {}}>
                            <TableCell>
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: document.uploadedBy !== 'admin' ? 700 : 400 }}>
                                  {document.uploadedBy !== 'admin' && document.title
                                    ? document.title
                                    : document.fileName}
                                </Typography>
                                {document.uploadedBy !== 'admin' && document.fileName && (
                                  <Typography variant="caption" color="text.secondary">{document.fileName}</Typography>
                                )}
                            </Box>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={(document.fileType?.split('/')[1] || document.fileType || '').toUpperCase()}
                                size="small"
                                variant="outlined"
                              />
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={document.uploadedBy === 'admin' ? 'Admin' : '📤 Élève'}
                                size="small"
                                color={document.uploadedBy === 'admin' ? 'default' : 'secondary'}
                                variant="outlined"
                              />
                            </TableCell>
                            <TableCell>
                              {document.note
                                ? <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>{document.note}</Typography>
                                : <Typography variant="caption" color="text.disabled">—</Typography>
                              }
                            </TableCell>
                            <TableCell>
                              <IconButton
                                onClick={() => navigate(`/documents/${activeTab}/${user.id}/${document.id}`)}
                                size="small"
                                title="Voir les détails"
                              >
                                <ViewIcon color="action" fontSize="small" />
                              </IconButton>
                              <IconButton href={document.downloadURL} target="_blank" download size="small">
                                <DownloadIcon color="primary" fontSize="small" />
                              </IconButton>
                              <IconButton onClick={() => handleDelete(user.id, document.id, document.storagePath, activeTab)} size="small">
                                <DeleteIcon color="error" fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              </Grid>
            );
          })}
        </Grid>
      </Box>
    );
  };

  // ─── Rendu : Comptabilité ─────────────────────────────────────────────────────

  const renderAccountingDocuments = () => {
    const cats = categories.accounting || [];
    const filteredDocs = accountingDocs.filter(d =>
      d.fileName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      d.categoryName?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 'bold' }}>Documents comptables</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <TextField
              label="Rechercher un document" variant="outlined" size="small"
              value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} sx={{ width: 300 }}
            />
            <Button variant="contained" startIcon={<AddIcon />}
              onClick={() => handleOpenDialog(null, 'accounting')}
              sx={{ bgcolor: '#7C3AED', '&:hover': { bgcolor: '#6D28D9' } }}>
              Ajouter un document
            </Button>
            <Button variant="outlined" startIcon={<AddIcon />}
              onClick={() => setIsAddingCategory(true)}>
              Nouvelle catégorie
            </Button>
          </Box>
        </Box>

        {isAddingCategory && (
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <TextField
              autoFocus label="Nom de la nouvelle catégorie" variant="outlined" size="small"
              value={newCategoryName} onChange={(e) => setNewCategoryName(e.target.value)}
              sx={{ flexGrow: 1, mr: 2 }}
            />
            <Button variant="contained" onClick={handleAddCategory} disabled={!newCategoryName.trim()}
              sx={{ bgcolor: '#7C3AED', '&:hover': { bgcolor: '#6D28D9' } }}>Ajouter</Button>
            <Button variant="outlined" onClick={() => setIsAddingCategory(false)} sx={{ ml: 1 }}>Annuler</Button>
          </Box>
        )}

        <Grid container spacing={3}>
          {cats.map(cat => {
            const catDocs = filteredDocs.filter(d => d.categoryId === cat.id);
            return (
              <Grid item xs={12} md={6} key={cat.id}>
                <Paper elevation={2} sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, borderBottom: '2px solid #7C3AED', pb: 2 }}>
                    <FolderIcon sx={{ mr: 1, color: '#7C3AED', fontSize: 30 }} />
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>{cat.name}</Typography>
                    <Chip label={`${catDocs.length} documents`} size="small" sx={{ ml: 2, bgcolor: '#7C3AED', color: 'white' }} />
                    <Button size="small" startIcon={<AddIcon />} sx={{ ml: 'auto' }}
                      onClick={() => handleOpenDialog(null, 'accounting', cat)}>
                      Ajouter
                    </Button>
                  </Box>
                  {catDocs.length === 0 ? (
                    <Typography variant="body1" sx={{ textAlign: 'center', py: 3, fontStyle: 'italic', color: 'text.secondary' }}>
                      Aucun document dans ce dossier
                    </Typography>
                  ) : (
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 'bold' }}>Document</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Type</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {catDocs.map(document => (
                            <TableRow key={document.id} hover>
                              <TableCell>{document.fileName}</TableCell>
                              <TableCell>
                                <Chip
                                  label={(document.fileType?.split('/')[1] || '').toUpperCase()}
                                  size="small" variant="outlined"
                                />
                              </TableCell>
                              <TableCell>
                                <IconButton
                                  onClick={() => navigate(`/documents/accounting/_/${document.id}`)}
                                  size="small"
                                  title="Voir les détails"
                                >
                                  <ViewIcon color="action" fontSize="small" />
                                </IconButton>
                                <IconButton href={document.downloadURL} target="_blank" download size="small">
                                  <DownloadIcon color="primary" fontSize="small" />
                                </IconButton>
                                <IconButton onClick={() => handleDelete(null, document.id, document.storagePath, 'accounting')} size="small">
                                  <DeleteIcon color="error" fontSize="small" />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </Paper>
              </Grid>
            );
          })}
        </Grid>
      </Box>
    );
  };

  // ─── Rendu principal ──────────────────────────────────────────────────────────

  const renderContent = () => {
    if (activeTab === 'accounting') return renderAccountingDocuments();
    if (activeTab === 'requests') return renderRequestsPanel();
    if (selectedCategory) return renderCategoryDocuments();
    return (
      <>
        {renderDocumentCategories()}
        {renderStudentsWithRequests()}
      </>
    );
  };

  const pendingCount = documentRequests.filter(r => r.status === 'pending').length;

  if (loading) {
    return (
      <Container style={{ textAlign: 'center', marginTop: '2rem' }}>
        <CircularProgress />
        <Typography>Chargement des documents...</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl">
      <Typography variant="h4" gutterBottom sx={{ mt: 3, mb: 4, fontWeight: 'bold' }}>
        Gestion des Documents
      </Typography>

      <Tabs
        value={activeTab}
        onChange={(e, v) => { setActiveTab(v); setSelectedCategory(null); setSearchTerm(''); }}
        sx={{ mb: 4, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab label="Élèves" value="students" />
        <Tab label="Professeurs" value="teachers" />
        <Tab label="Comptabilité" value="accounting" />
        <Tab
          value="requests"
          label={
            <Badge badgeContent={pendingCount} color="warning" max={99}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <RequestPageIcon fontSize="small" />
                Demandes
              </Box>
            </Badge>
          }
        />
      </Tabs>

      {renderContent()}

      {/* ── Dialog upload ── */}
      <Dialog open={openDialog} onClose={handleCloseDialog} fullWidth maxWidth="sm">
        <DialogTitle>
          {selectedItem?.type === 'accounting'
            ? 'Ajouter un document comptable'
            : `Ajouter un document pour ${selectedItem
                ? (selectedItem.type === 'students' ? students : teachers)
                    .find(u => u.id === selectedItem.id)
                    ? `${(selectedItem.type === 'students' ? students : teachers).find(u => u.id === selectedItem.id).firstName} ${(selectedItem.type === 'students' ? students : teachers).find(u => u.id === selectedItem.id).lastName}`
                    : ''
                : ''
              }`
          }
        </DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2, mb: 2 }}>
            <InputLabel>Catégorie</InputLabel>
            <Select
              value={documentCategory}
              label="Catégorie"
              onChange={(e) => setDocumentCategory(e.target.value)}
            >
              {(categories[selectedItem?.type || activeTab] || []).map(cat => (
                <MenuItem key={cat.id} value={cat.id}>{cat.name}</MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Visibilité utilisateur — uniquement pour élèves/profs */}
          {selectedItem?.type !== 'accounting' && (
            <FormControlLabel
              sx={{ mb: 2, display: 'flex', alignItems: 'center' }}
              control={
                <Switch
                  checked={visibleByUser}
                  onChange={(e) => setVisibleByUser(e.target.checked)}
                  color="secondary"
                />
              }
              label={
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Visible par l'utilisateur dans l'application
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {visibleByUser
                      ? "✅ L'utilisateur pourra voir et télécharger ce document depuis l'app"
                      : "🔒 Document interne uniquement (non visible par l'utilisateur)"}
                  </Typography>
                </Box>
              }
            />
          )}

          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Formats acceptés : PDF, Excel, images (JPG, PNG, etc.)
          </Typography>
          <input
            type="file"
            accept="*/*"
            onChange={(e) => setFile(e.target.files[0])}
            style={{ marginTop: '0.5rem', display: 'block' }}
          />
          {file && (
            <Alert severity="info" sx={{ mt: 2 }}>
              Fichier sélectionné : <strong>{file.name}</strong> ({(file.size / 1024).toFixed(1)} Ko)
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={uploading}>Annuler</Button>
          <Button onClick={handleUpload} disabled={!file || uploading || !documentCategory}
            variant="contained" color="primary">
            {uploading ? <CircularProgress size={24} /> : 'Uploader'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Dialog demande de document ── */}
      <Dialog open={openRequestDialog} onClose={() => setOpenRequestDialog(false)} fullWidth maxWidth="sm">
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <RequestPageIcon sx={{ color: '#7C3AED' }} />
            Demander un document
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedStudentForRequest && (
            <Alert
              severity="info"
              icon={selectedStudentForRequest.isMinor ? <ChildCareIcon /> : <PersonIcon />}
              sx={{ mb: 2 }}
            >
              <Typography variant="body2">
                <strong>{selectedStudentForRequest.firstName} {selectedStudentForRequest.lastName}</strong>
                {selectedStudentForRequest.isMinor
                  ? ` — Élève mineur. La demande sera gérée par le parent/tuteur${selectedStudentForRequest.guardianName ? ` (${selectedStudentForRequest.guardianName})` : ''} dans l'application.`
                  : ` — Élève adulte. La demande sera visible directement dans son application.`
                }
              </Typography>
            </Alert>
          )}

          <FormControl fullWidth sx={{ mt: 1, mb: 2 }}>
            <InputLabel>Type de document</InputLabel>
            <Select
              value={requestType}
              label="Type de document"
              onChange={(e) => setRequestType(e.target.value)}
            >
              {PREDEFINED_REQUEST_TYPES.map(t => (
                <MenuItem key={t.id} value={t.id}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <span>{t.icon}</span>
                    {t.label}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {requestType === 'autre' && (
            <TextField
              fullWidth
              label="Préciser le document demandé"
              variant="outlined"
              value={requestCustomLabel}
              onChange={(e) => setRequestCustomLabel(e.target.value)}
              sx={{ mb: 2 }}
            />
          )}

          <TextField
            fullWidth
            label="Note pour l'utilisateur (optionnel)"
            variant="outlined"
            multiline
            rows={3}
            value={requestNote}
            onChange={(e) => setRequestNote(e.target.value)}
            placeholder="Ex : Merci de fournir votre attestation d'assurance à jour..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenRequestDialog(false)}>Annuler</Button>
          <Button
            onClick={handleCreateRequest}
            disabled={!requestType || (requestType === 'autre' && !requestCustomLabel.trim())}
            variant="contained"
            sx={{ bgcolor: '#7C3AED', '&:hover': { bgcolor: '#6D28D9' } }}
          >
            Envoyer la demande
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Dialog modification catégorie ── */}
      <Dialog open={Boolean(editCategory)} onClose={() => setEditCategory(null)}>
        <DialogTitle>Modifier la catégorie</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus margin="dense" label="Nom de la catégorie" fullWidth
            value={editCategory?.name || ''}
            onChange={(e) => setEditCategory({ ...editCategory, name: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditCategory(null)}>Annuler</Button>
          <Button onClick={handleEditCategory} variant="contained">Enregistrer</Button>
        </DialogActions>
      </Dialog>

      {/* ── Snackbar feedback ── */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(s => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default Documents;